import { useMemo } from "react";
import ChartDataLabels from "chartjs-plugin-datalabels";
import PropTypes from "prop-types";
import { GlobalStyles } from "@mui/material";

// react-chartjs-2 components
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// ReportsBarChart configurations
import configs from "examples/Charts/BarCharts/ReportsBarChart/configs";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function FeedChart({
  color,
  title,
  chart,
  chartHeight,
  horizontal,
  feed,
  feed_total,
  showTotal,
  titleSx,
  timestamp,
  fcr,
  fcr_day,
  fcr_target,
  fcr_message,
  fcr_status,
  fcr_deviationp,
}) {
  const { data, options } = configs(chart.labels || [], chart.datasets || {});
  const chartOptions = useMemo(
    () => (horizontal ? { ...options, indexAxis: "y" } : options),
    [horizontal, options]
  );

  const formatNumber = (value, fractionDigits = 2) => {
    if (value === null || value === undefined || value === "") return "--";
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return String(value);
    return numeric.toLocaleString(undefined, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
  };

  return (
    <Card sx={{ height: "100%", position: "relative" }}>
      <MDBox padding="1rem">
        {useMemo(
          () => (
            <MDBox
              variant="gradient"
              bgColor={color}
              borderRadius="lg"
              coloredShadow={color}
              py={2}
              pr={0.5}
              mt={-5}
              height={chartHeight}
            >
              <Bar data={data} options={chartOptions} redraw />
            </MDBox>
          ),
          [color, chart, chartOptions]
        )}

        <MDBox pt={3} pb={1} px={1}>
          <MDTypography variant="h6" textTransform="capitalize" sx={titleSx}>
            {title}
          </MDTypography>
          {showTotal ? (
            <MDTypography variant="h6" textTransform="capitalize">
              Total Feed: {formatNumber(feed_total, 0)}kg
            </MDTypography>
          ) : null}
        </MDBox>

        <GlobalStyles
          styles={`
            @keyframes scroll-left {
              0% { transform: translateX(100%); }
              100% { transform: translateX(-100%); }
            }
          `}
        />

        <MDBox
          position="absolute"
          bottom={45}
          right={16}
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          gap={0.5}
        >
          {fcr != null && (
            <MDBox display="flex" alignItems="center" sx={{ width: "100%" }}>
              <MDTypography sx={{ fontSize: "1rem", minWidth: "65px" }} color="textSecondary">
                FCR:
              </MDTypography>
              <MDBox
                component="span"
                sx={{
                  px: 1.2,
                  py: 0.2,
                  mx: 0.5,
                  borderRadius: "4px",
                  backgroundColor: (theme) => {
                    switch ((fcr_status || "").toLowerCase()) {
                      case "green":
                        return theme.palette.success.main;
                      case "amber":
                        return theme.palette.warning.main;
                      case "red":
                        return theme.palette.error.main;
                      default:
                        return theme.palette.grey[400];
                    }
                  },
                  color: (theme) => theme.palette.common.white,
                  fontWeight: "bold",
                  fontSize: "1rem",
                  ml: "auto",
                }}
              >
                {formatNumber(fcr, 3)}
              </MDBox>
            </MDBox>
          )}

          {fcr_target != null && (
            <MDBox display="flex" alignItems="center" gap={0.5} sx={{ width: "100%" }}>
              <MDTypography sx={{ fontSize: "1rem", minWidth: "65px" }} color="textSecondary">
                Target:
              </MDTypography>
              <MDTypography sx={{ fontSize: "1rem", ml: 0 }} color="textSecondary">
                {formatNumber(fcr_target, 1)}
              </MDTypography>
            </MDBox>
          )}

          {fcr_message && (
            <MDBox
              sx={{
                overflow: "hidden",
                whiteSpace: "nowrap",
                maxWidth: "180px",
                ml: "auto",
                position: "relative",
              }}
            >
              <MDTypography
                sx={{
                  display: "inline-block",
                  fontSize: "1rem",
                  color: "text.error",
                  animation: "scroll-left 10s linear infinite",
                }}
              >
                {fcr_message}
              </MDTypography>
            </MDBox>
          )}
        </MDBox>

        {timestamp ? (
          <MDBox position="absolute" bottom={8} left={16} display="flex" alignItems="center">
            <MDTypography variant="button" color="text" lineHeight={1} sx={{ mt: 0.15, mr: 0.5 }}>
              <Icon>schedule</Icon>
            </MDTypography>
            <MDTypography variant="button" color="text" fontWeight="light">
              {timestamp}
            </MDTypography>
          </MDBox>
        ) : null}
      </MDBox>
    </Card>
  );
}

// Setting default values for the props of ReportsBarChart
FeedChart.defaultProps = {
  color: "info",
  description: "",
  chartHeight: "12.5rem",
  horizontal: false,
  showTotal: true,
  titleSx: {},
};

// Typechecking props for the ReportsBarChart
FeedChart.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  chart: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array, PropTypes.object])).isRequired,
  chartHeight: PropTypes.string,
  horizontal: PropTypes.bool,
  timestamp: PropTypes.string,
  feed: PropTypes.number,
  feed_total: PropTypes.number,
  showTotal: PropTypes.bool,
  titleSx: PropTypes.object,
  fcr: PropTypes.number,
  fcr_day: PropTypes.string,
  fcr_target: PropTypes.number,
  fcr_message: PropTypes.string,
  fcr_status: PropTypes.string,
  fcr_deviationp: PropTypes.number,
};

export default FeedChart;
